// REACT_APP_API_BASE_URL controls where the SPA calls the API:
//   • unset            → http://localhost:8000   (local dev)
//   • "" (empty)       → same origin              (VPS behind nginx, or Cloudflare /api proxy)
//   • "https://api…"   → absolute backend URL     (Cloudflare Pages + separate API host)
// Uses ?? so an explicit empty string is honoured as "same origin".
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
export const API_PREFIX = '/api/v1';

const p = (path) => `${API_PREFIX}${path}`;

export const ENDPOINTS = {
  // ── Platform super-admin (operates across all organizations) ──
  ADMIN: {
    STATS:          p('/admin/stats'),                              // dashboard KPIs
    ORGANIZATIONS:  p('/admin/organizations'),                      // list (search/status/plan)
    ORG_GET:        (id) => p(`/admin/organizations/${id}`),
    ORG_UPDATE:     (id) => p(`/admin/organizations/${id}`),
    ORG_SUSPEND:    (id) => p(`/admin/organizations/${id}/suspend`),
    ORG_ACTIVATE:   (id) => p(`/admin/organizations/${id}/activate`),
    ORG_USERS:      (id) => p(`/admin/organizations/${id}/users`),
    ORG_SUBSCRIPTION: (id) => p(`/admin/organizations/${id}/subscription`),               // GET full sub detail
    ORG_EXTEND_TRIAL: (id) => p(`/admin/organizations/${id}/subscription/extend-trial`),   // POST { days }
    ORG_CANCEL_SUB:   (id) => p(`/admin/organizations/${id}/subscription/cancel`),         // POST -> cancel now
    ORG_UPDATE_SUB:   (id) => p(`/admin/organizations/${id}/subscription/update`),         // POST { plan, seats, comp, comp_until }
    ORG_ADDONS:       (id) => p(`/admin/organizations/${id}/addons`),                      // GET list / POST { addon, comp_until?, tier? } (org must be on Pro; tier only applies to outbound_api)
    ORG_ADDON:        (id, addon) => p(`/admin/organizations/${id}/addons/${addon}`),      // DELETE -> revoke immediately
    ORG_INVOICES:     (id) => p(`/admin/organizations/${id}/invoices`),                    // GET ?limit=
    API_TIERS:        p('/admin/api-tiers'),                                               // GET Inventory API tier catalog (key, name, price, quota, max_keys) - for the outbound_api comp tier picker
    // Manual account book - orgs that pay cash/offline instead of through Stripe
    ACCOUNTS:         p('/admin/accounts'),                                                // GET all orgs with a ledger + computed balance/overdue
    ORG_LEDGER:          (id) => p(`/admin/organizations/${id}/ledger`),                   // GET entries / POST add entry
    ORG_LEDGER_SUMMARY:  (id) => p(`/admin/organizations/${id}/ledger/summary`),           // GET balance/overdue for one org
    ORG_LEDGER_ENTRY:    (id, entryId) => p(`/admin/organizations/${id}/ledger/${entryId}`), // DELETE
    ORG_LEDGER_REMIND:   (id) => p(`/admin/organizations/${id}/ledger/send-reminder`),      // POST { amount_unpaid, overdue_balance, to_email?, custom_message? }
    USERS:          p('/admin/users'),                              // users across all orgs
    USER_SUSPEND:   (id) => p(`/admin/users/${id}/suspend`),         // is_active = false
    USER_ACTIVATE:  (id) => p(`/admin/users/${id}/activate`),
    USER_REVOKE:    (id) => p(`/admin/users/${id}/revoke-tokens`),   // kill all sessions
    SUBSCRIPTIONS:  p('/admin/subscriptions'),                      // billing / plans
    PLANS:          p('/admin/plans'),
    // Platform-wide announcements / notifications
    NOTIFICATIONS:  p('/admin/notifications'),                       // GET list, POST create/broadcast
    NOTIFICATION:   (id) => p(`/admin/notifications/${id}`),
    // Firestore-like app settings (key -> JSON document)
    SETTINGS:       p('/admin/settings'),                            // GET all docs
    SETTING:        (key) => p(`/admin/settings/${key}`),            // GET / PUT (upsert) / DELETE
  },
  AUTH: {
    REGISTER: p('/auth/register'),
    LOGIN: p('/auth/login'),
    LOGOUT: p('/auth/logout'),
    REFRESH: p('/auth/refresh'),
    ME: p('/auth/me'),
    CHANGE_PASSWORD: p('/auth/change-password'),
    FORGOT_PASSWORD: p('/auth/forgot-password'),
    RESET_PASSWORD: p('/auth/reset-password'),
    REQUEST_EMAIL_CHANGE: p('/auth/request-email-change'),
    VERIFY_EMAIL_CHANGE: p('/auth/verify-email-change'),
  },
  USERS: {
    LIST: p('/users'),
    CREATE: p('/users'),
    GET: (id) => p(`/users/${id}`),
    UPDATE: (id) => p(`/users/${id}`),
    DELETE: (id) => p(`/users/${id}`),
    PERMISSIONS: (id) => p(`/users/${id}/permissions`),
  },
  INVITATIONS: {
    LIST: p('/invitations'),
    CREATE: p('/invitations'),
    RESEND: (id) => p(`/invitations/${id}/resend`),
    REVOKE: (id) => p(`/invitations/${id}`),
    LOOKUP: (token) => p(`/invitations/lookup/${token}`),
    ACCEPT: p('/invitations/accept'),
  },
  ORGANIZATIONS: {
    CURRENT: p('/organizations/current'),
    UPDATE: p('/organizations/current'),
    LOGO: p('/organizations/current/logo'),
  },
  BRANCHES: {
    LIST: p('/branches'),
    CREATE: p('/branches'),
    GET: (id) => p(`/branches/${id}`),
    UPDATE: (id) => p(`/branches/${id}`),
  },
  JEWELRY: {
    LIST:   p('/jewelry'),
    CREATE: p('/jewelry'),
    GET:    (id) => p(`/jewelry/${id}`),
    UPDATE: (id) => p(`/jewelry/${id}`),
    DELETE: (id) => p(`/jewelry/${id}`),
  },
  VENDORS: {
    LIST: p('/vendors'),
    CREATE: p('/vendors'),
    GET: (id) => p(`/vendors/${id}`),
    UPDATE: (id) => p(`/vendors/${id}`),
  },
  VENDOR_PAYMENTS: {
    LIST: p('/vendor-payments'),
    CREATE: p('/vendor-payments'),
    GET: (id) => p(`/vendor-payments/${id}`),
    CANCEL: (id) => p(`/vendor-payments/${id}/cancel`),   // soft cancel - keeps the record
  },
  EXPENSES: {
    LIST:    p('/expenses'),
    CREATE:  p('/expenses'),
    GET:     (id) => p(`/expenses/${id}`),
    UPDATE:  (id) => p(`/expenses/${id}`),
    DELETE:  (id) => p(`/expenses/${id}`),
    APPROVE: (id) => p(`/expenses/${id}/approve`),
    REJECT:  (id) => p(`/expenses/${id}/reject`),
    RECEIPT_UPLOAD: p('/expenses/receipt-upload'),   // multipart -> { url }
  },
  STONES: {
    LIST: p('/stones'),
    CREATE: p('/stones'),
    GET: (id) => p(`/stones/${id}`),
    UPDATE: (id) => p(`/stones/${id}`),
    DELETE: (id) => p(`/stones/${id}`),
    BULK_IMPORT: p('/stones/import/bulk'),
    HOLD: (id) => p(`/stones/${id}/hold`),
    RELEASE_HOLD: (id) => p(`/stones/${id}/release-hold`),
    DOCUMENTS: (id) => p(`/stones/${id}/documents`),
  },
  BARCODE_PRINTS: {
    LIST: p('/barcode-prints'),
    CREATE: p('/barcode-prints'),
  },
  TRANSFERS: {
    LIST: p('/transfers'),
    CREATE: p('/transfers'),
    GET: (id) => p(`/transfers/${id}`),
    SHIP: (id) => p(`/transfers/${id}/ship`),
    CONFIRM_RECEIPT: (id) => p(`/transfers/${id}/confirm-receipt`),
  },
  MEMOS: {
    LIST: p('/memos'),
    CREATE: p('/memos'),
    GET: (id) => p(`/memos/${id}`),
    UPDATE: (id) => p(`/memos/${id}`),
    CANCEL: (id) => p(`/memos/${id}/cancel`),   // soft cancel - releases stones, keeps record
    RETURN: (id) => p(`/memos/${id}/return`),
    CONVERT_INVOICE: (id) => p(`/memos/${id}/convert-invoice`),
  },
  INVOICES: {
    LIST: p('/invoices'),
    CREATE: p('/invoices'),
    GET: (id) => p(`/invoices/${id}`),
    UPDATE: (id) => p(`/invoices/${id}`),
    CANCEL: (id) => p(`/invoices/${id}/cancel`),   // soft cancel - releases stone, recomputes status
    DELETE: (id) => p(`/invoices/${id}`),
    MARK_PAID: (id) => p(`/invoices/${id}/mark-paid`),
  },
  ADDITIONAL_CHARGES: {
    LIST: p('/additional-charges'),
    CREATE: p('/additional-charges'),
    GET: (id) => p(`/additional-charges/${id}`),
    MARK_PAID: (id) => p(`/additional-charges/${id}/mark-paid`),
  },
  SALES_RETURNS: {
    LIST: p('/sales-returns'),
    CREATE: p('/sales-returns'),
    GET: (id) => p(`/sales-returns/${id}`),
    UPDATE: (id) => p(`/sales-returns/${id}`),
    CANCEL: (id) => p(`/sales-returns/${id}/cancel`),  // soft cancel - reverses stones + invoice
    DELETE: (id) => p(`/sales-returns/${id}`),          // hard delete (owner only, no reversal)
  },
  MEMO_RETURNS: {
    LIST: p('/memo-returns'),
    CREATE: p('/memo-returns'),
    GET: (id) => p(`/memo-returns/${id}`),
    UPDATE: (id) => p(`/memo-returns/${id}`),
    CANCEL: (id) => p(`/memo-returns/${id}/cancel`),
    DELETE: (id) => p(`/memo-returns/${id}`),
  },
  CUSTOMERS: {
    LIST: p('/customers'),
    CREATE: p('/customers'),
    GET: (id) => p(`/customers/${id}`),
    UPDATE: (id) => p(`/customers/${id}`),
    VERIFY_KYC: (id) => p(`/customers/${id}/verify-kyc`),
    CREDIT: (id) => p(`/customers/${id}/credit`),
  },
  AUDIT_LOGS: {
    LIST: p('/audit-logs'),
  },
  REPORTS: {
    DASHBOARD: p('/reports/dashboard'),
    REVENUE: p('/reports/revenue'),
    AR_AGING: p('/reports/ar-aging'),
    TAX_SUMMARY: p('/reports/tax-summary'),
    INVENTORY: p('/reports/inventory'),
    SALES_PARTY_WISE: p('/reports/sales-party-wise'),
    OUTSTANDING_MEMO: p('/reports/outstanding-memo'),
    DIAMOND_MOVEMENT: p('/reports/diamond-movement'),
    MEMO_TRANSACTIONS: p('/reports/memo-transactions'),
    JEWELRY_STOCK: p('/reports/jewelry-stock'),
    STOCK_SUMMARY: p('/reports/stock-summary'),
  },
};
